import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsApi } from '../../utils/api';
import {
    BarChart3,
    Loader2,
    Sparkles,
    TrendingUp,
    TrendingDown,
    Users,
    Eye,
    Clock,
    Globe,
    FileText,
    Download,
    Lightbulb,
    RefreshCw,
    Calendar,
    ArrowUpRight,
    Target,
    Search,
    Share2,
    Megaphone,
    Mail,
    DollarSign,
    Heart,
    MousePointer
} from 'lucide-react';

export default function AnalyticsModule() {
    const { projectId } = useParams();
    const queryClient = useQueryClient();
    const [insightFocus, setInsightFocus] = useState('');
    const [activeModuleTab, setActiveModuleTab] = useState<'overview' | 'seo' | 'social' | 'ads' | 'email'>('overview');

    // Fetch dashboard data (unified from all modules)
    const { data: dashboardData, isLoading: dashboardLoading, refetch: refetchDashboard } = useQuery({
        queryKey: ['analytics-dashboard', projectId],
        queryFn: () => analyticsApi.getDashboard(projectId!),
        enabled: !!projectId
    });

    // Fetch module-specific analytics
    const { data: moduleData, isLoading: moduleLoading } = useQuery({
        queryKey: ['analytics-module', activeModuleTab, projectId],
        queryFn: () => analyticsApi.getModuleAnalytics(activeModuleTab as any, projectId!),
        enabled: !!projectId && activeModuleTab !== 'overview'
    });

    // Fetch reports
    const { data: reportsData, isLoading: reportsLoading } = useQuery({
        queryKey: ['analytics-reports', projectId],
        queryFn: () => analyticsApi.listReports(projectId!),
        enabled: !!projectId
    });

    // Get AI insights
    const insightsMutation = useMutation({
        mutationFn: (focus?: string) => analyticsApi.getInsights(projectId!, focus),
    });

    // Generate report
    const generateReportMutation = useMutation({
        mutationFn: (data: any) => analyticsApi.generateReport(projectId!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['analytics-reports', projectId] });
        }
    });

    // Parse unified dashboard data from backend
    const dashboard = dashboardData?.data?.dashboard || {
        overview: { totalTraffic: 0, totalConversions: 0, totalRevenue: 0 },
        seo: { organicTraffic: 0, keywordsRanking: 0, avgPosition: 0, score: 0, auditsCount: 0 },
        social: { totalReach: 0, engagement: 0, postsCount: 0, scheduledCount: 0 },
        ads: { spend: 0, conversions: 0, roas: '0.00', campaignsCount: 0, activeCampaigns: 0 },
        email: { sent: 0, openRate: '0.00', clickRate: '0.00', campaignsCount: 0 },
        lastUpdated: null
    };
    const reports = reportsData?.data?.reports || [];
    const insights = insightsMutation.data?.data?.insights || [];
    const moduleAnalytics = moduleData?.data?.data || {};

    const moduleTabs = [
        { key: 'overview', label: 'Overview', icon: BarChart3 },
        { key: 'seo', label: 'SEO', icon: Search },
        { key: 'social', label: 'Social', icon: Share2 },
        { key: 'ads', label: 'Ads', icon: Megaphone },
        { key: 'email', label: 'Email', icon: Mail },
    ];

    const formatDuration = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const getTrendIcon = (trend: number) => {
        if (trend > 0) return <TrendingUp className="w-4 h-4 text-green-400" />;
        if (trend < 0) return <TrendingDown className="w-4 h-4 text-red-400" />;
        return null;
    };

    const getTrendColor = (trend: number) => {
        if (trend > 0) return 'text-green-400';
        if (trend < 0) return 'text-red-400';
        return 'text-dark-400';
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <BarChart3 className="w-8 h-8 text-purple-400" />
                        Analytics Dashboard
                    </h1>
                    <p className="text-dark-400 mt-1">Unified analytics across all marketing modules</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => refetchDashboard()}
                        className="btn btn-secondary p-2"
                        title="Refresh Data"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => insightsMutation.mutate(insightFocus)}
                        disabled={insightsMutation.isPending}
                        className="btn btn-secondary flex items-center gap-2"
                    >
                        {insightsMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Sparkles className="w-4 h-4" />
                        )}
                        AI Insights
                    </button>
                    <button
                        onClick={() => generateReportMutation.mutate({ type: 'weekly' })}
                        disabled={generateReportMutation.isPending}
                        className="btn btn-primary flex items-center gap-2"
                    >
                        {generateReportMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <FileText className="w-4 h-4" />
                        )}
                        Generate Report
                    </button>
                </div>
            </div>

            {/* Module Tabs */}
            <div className="flex gap-2 border-b border-dark-800 pb-2">
                {moduleTabs.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveModuleTab(tab.key as any)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${activeModuleTab === tab.key
                            ? 'bg-primary-500/20 text-primary-400'
                            : 'hover:bg-dark-800 text-dark-400'
                            }`}
                    >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Stats Overview */}
            {dashboardLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                </div>
            ) : activeModuleTab === 'overview' ? (
                <>
                    {/* Overview Stats */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="card p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-dark-400 text-sm">Total Traffic</p>
                                    <p className="text-3xl font-bold mt-1">{dashboard.overview?.totalTraffic?.toLocaleString() || '0'}</p>
                                </div>
                                <Globe className="w-10 h-10 text-blue-400 opacity-50" />
                            </div>
                        </div>
                        <div className="card p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-dark-400 text-sm">Total Conversions</p>
                                    <p className="text-3xl font-bold mt-1">{dashboard.overview?.totalConversions?.toLocaleString() || '0'}</p>
                                </div>
                                <Target className="w-10 h-10 text-green-400 opacity-50" />
                            </div>
                        </div>
                        <div className="card p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-dark-400 text-sm">Total Revenue</p>
                                    <p className="text-3xl font-bold mt-1">${dashboard.overview?.totalRevenue?.toLocaleString() || '0'}</p>
                                </div>
                                <DollarSign className="w-10 h-10 text-emerald-400 opacity-50" />
                            </div>
                        </div>
                    </div>

                    {/* Module Summary Cards */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* SEO Card */}
                        <div className="card p-6">
                            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                                <Search className="w-5 h-5 text-blue-400" />
                                SEO Performance
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-dark-400 text-sm">Organic Traffic</p>
                                    <p className="text-xl font-bold">{dashboard.seo?.organicTraffic?.toLocaleString() || '0'}</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Keywords Ranking</p>
                                    <p className="text-xl font-bold">{dashboard.seo?.keywordsRanking || 0}</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Avg Position</p>
                                    <p className="text-xl font-bold">{dashboard.seo?.avgPosition || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Health Score</p>
                                    <p className="text-xl font-bold text-green-400">{dashboard.seo?.score || 0}%</p>
                                </div>
                            </div>
                        </div>

                        {/* Social Card */}
                        <div className="card p-6">
                            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                                <Share2 className="w-5 h-5 text-pink-400" />
                                Social Media
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-dark-400 text-sm">Total Reach</p>
                                    <p className="text-xl font-bold">{dashboard.social?.totalReach?.toLocaleString() || '0'}</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Engagement</p>
                                    <p className="text-xl font-bold">{dashboard.social?.engagement?.toLocaleString() || '0'}</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Posts Published</p>
                                    <p className="text-xl font-bold">{dashboard.social?.postsCount || 0}</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Scheduled</p>
                                    <p className="text-xl font-bold text-blue-400">{dashboard.social?.scheduledCount || 0}</p>
                                </div>
                            </div>
                        </div>

                        {/* Ads Card */}
                        <div className="card p-6">
                            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                                <Megaphone className="w-5 h-5 text-orange-400" />
                                Advertising
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-dark-400 text-sm">Total Spend</p>
                                    <p className="text-xl font-bold">${dashboard.ads?.spend?.toLocaleString() || '0'}</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Conversions</p>
                                    <p className="text-xl font-bold">{dashboard.ads?.conversions || 0}</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">ROAS</p>
                                    <p className="text-xl font-bold text-green-400">{dashboard.ads?.roas || '0.00'}x</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Active Campaigns</p>
                                    <p className="text-xl font-bold">{dashboard.ads?.activeCampaigns || 0}/{dashboard.ads?.campaignsCount || 0}</p>
                                </div>
                            </div>
                        </div>

                        {/* Email Card */}
                        <div className="card p-6">
                            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                                <Mail className="w-5 h-5 text-green-400" />
                                Email Marketing
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-dark-400 text-sm">Emails Sent</p>
                                    <p className="text-xl font-bold">{dashboard.email?.sent?.toLocaleString() || '0'}</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Open Rate</p>
                                    <p className="text-xl font-bold">{dashboard.email?.openRate || '0.00'}%</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Click Rate</p>
                                    <p className="text-xl font-bold">{dashboard.email?.clickRate || '0.00'}%</p>
                                </div>
                                <div>
                                    <p className="text-dark-400 text-sm">Campaigns</p>
                                    <p className="text-xl font-bold">{dashboard.email?.campaignsCount || 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                /* Module-Specific Analytics */
                <div className="card p-6">
                    {moduleLoading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold capitalize">{activeModuleTab} Detailed Analytics</h3>

                            <div className="grid grid-cols-4 gap-4">
                                {activeModuleTab === 'seo' && (
                                    <>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Total Audits</p>
                                            <p className="text-2xl font-bold">{moduleAnalytics.totalAudits || 0}</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Latest Score</p>
                                            <p className="text-2xl font-bold text-green-400">{moduleAnalytics.latestScore || '-'}%</p>
                                        </div>
                                    </>
                                )}
                                {activeModuleTab === 'social' && (
                                    <>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Total Posts</p>
                                            <p className="text-2xl font-bold">{moduleAnalytics.totalPosts || 0}</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Published</p>
                                            <p className="text-2xl font-bold text-green-400">{moduleAnalytics.publishedPosts || 0}</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Engagement Rate</p>
                                            <p className="text-2xl font-bold">{moduleAnalytics.engagementRate || '0.00'}%</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Total Reach</p>
                                            <p className="text-2xl font-bold">{moduleAnalytics.totalMetrics?.reach?.toLocaleString() || 0}</p>
                                        </div>
                                    </>
                                )}
                                {activeModuleTab === 'ads' && (
                                    <>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Total Campaigns</p>
                                            <p className="text-2xl font-bold">{moduleAnalytics.totalCampaigns || 0}</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Active</p>
                                            <p className="text-2xl font-bold text-green-400">{moduleAnalytics.activeCampaigns || 0}</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">CTR</p>
                                            <p className="text-2xl font-bold">{moduleAnalytics.ctr || '0.00'}%</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">ROAS</p>
                                            <p className="text-2xl font-bold text-emerald-400">{moduleAnalytics.roas || '0.00'}x</p>
                                        </div>
                                    </>
                                )}
                                {activeModuleTab === 'email' && (
                                    <>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Total Campaigns</p>
                                            <p className="text-2xl font-bold">{moduleAnalytics.totalCampaigns || 0}</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Sent</p>
                                            <p className="text-2xl font-bold text-green-400">{moduleAnalytics.sentCampaigns || 0}</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Open Rate</p>
                                            <p className="text-2xl font-bold">{moduleAnalytics.openRate || '0.00'}%</p>
                                        </div>
                                        <div className="p-4 bg-dark-800 rounded-lg">
                                            <p className="text-dark-400 text-sm">Click Rate</p>
                                            <p className="text-2xl font-bold">{moduleAnalytics.clickRate || '0.00'}%</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* AI Insights */}
            {insights.length > 0 && (
                <div className="card p-6 border-primary-500/30">
                    <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                        <Lightbulb className="w-5 h-5 text-yellow-400" />
                        AI Insights
                    </h3>
                    <div className="space-y-3">
                        {insights.map((insight: any, i: number) => (
                            <div key={i} className="p-4 bg-dark-800/50 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="w-5 h-5 text-primary-400 mt-0.5" />
                                    <div>
                                        <p className="font-medium">{insight.title || 'Insight'}</p>
                                        <p className="text-dark-400 text-sm mt-1">{insight.description || insight}</p>
                                        {insight.recommendation && (
                                            <p className="text-primary-400 text-sm mt-2">
                                                💡 {insight.recommendation}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Quick Insight Topics */}
            <div className="card p-6">
                <h3 className="text-lg font-semibold mb-4">Get Insights On</h3>
                <div className="flex flex-wrap gap-2">
                    {['Traffic Sources', 'User Behavior', 'Conversion Optimization', 'Content Performance', 'SEO Opportunities', 'Growth Tactics'].map((topic) => (
                        <button
                            key={topic}
                            onClick={() => {
                                setInsightFocus(topic);
                                insightsMutation.mutate(topic);
                            }}
                            className="px-4 py-2 bg-dark-800 hover:bg-dark-700 rounded-lg text-sm transition-colors"
                        >
                            {topic}
                        </button>
                    ))}
                </div>
            </div>

            {/* Reports */}
            <div className="card">
                <div className="p-4 border-b border-dark-800 flex items-center justify-between">
                    <h3 className="font-semibold">Generated Reports</h3>
                    <button
                        onClick={() => queryClient.invalidateQueries({ queryKey: ['analytics-reports', projectId] })}
                        className="text-dark-400 hover:text-white transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
                {reportsLoading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                    </div>
                ) : reports.length === 0 ? (
                    <div className="p-12 text-center">
                        <FileText className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                        <p className="text-dark-400 mb-4">No reports generated yet</p>
                        <button
                            onClick={() => generateReportMutation.mutate({ type: 'weekly' })}
                            disabled={generateReportMutation.isPending}
                            className="btn btn-primary inline-flex items-center gap-2"
                        >
                            {generateReportMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <FileText className="w-4 h-4" />
                            )}
                            Generate First Report
                        </button>
                    </div>
                ) : (
                    <div className="divide-y divide-dark-800">
                        {reports.map((report: any) => (
                            <div key={report.id} className="p-4 flex items-center justify-between hover:bg-dark-800/30">
                                <div className="flex items-center gap-4">
                                    <div className="p-2 rounded-lg bg-primary-500/20">
                                        <FileText className="w-5 h-5 text-primary-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{report.title || `${report.type} Report`}</p>
                                        <p className="text-dark-400 text-sm flex items-center gap-2">
                                            <Calendar className="w-3 h-3" />
                                            {new Date(report.createdAt).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                                <button className="btn btn-secondary btn-sm flex items-center gap-1">
                                    <Download className="w-3 h-3" />
                                    Download
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
